import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ExplanationDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Next-step controls (e.g. a contact link) shown under the explanation; the
   * header close button stays the way to dismiss the dialog. */
  actions?: ReactNode;
}

/** Modale di sola spiegazione, condivisa da `InfoChip` (etichetta cliccabile)
 * e da `InfoExplainer` (icona accanto a un dato): stesso contenitore, due
 * affordance diverse a seconda che la spiegazione appartenga a un'etichetta o
 * a una colonna già leggibile per conto suo. */
export function ExplanationDialog({
  open,
  onClose,
  title,
  children,
  actions,
}: ExplanationDialogProps) {
  const { t } = useTranslation('common');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoOutlinedIcon color="info" fontSize="small" />
        {/* `component="span"`: `DialogTitle` è già un `h2`, e un `h6` annidato
            dentro un heading è markup non valido. */}
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
          {title}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t('close')}
          sx={{ minWidth: 48, minHeight: 48 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* `div`: la spiegazione può contenere paragrafi e chip, che dentro un `p` sono markup non valido. */}
        <Typography component="div" variant="body2" color="text.secondary">
          {children}
        </Typography>
      </DialogContent>
      {actions && <DialogActions sx={{ px: 3, py: 2 }}>{actions}</DialogActions>}
    </Dialog>
  );
}
